import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Building2 } from 'lucide-react';

const Contact = () => {
  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-block border-l-4 border-orange-500 pl-4 mb-4">
              <p className="text-orange-500 font-oswald tracking-widest text-sm">CONTACT US</p>
            </div>
            <h1 className="font-oswald font-bold text-5xl md:text-6xl mb-6">GET IN TOUCH</h1>
            <p className="text-xl text-slate-300 max-w-3xl">
              Ready to discuss your project? Our team is here to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-oswald font-bold text-4xl text-slate-900 mb-8">CONTACT INFORMATION</h2>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4 p-6 bg-slate-50 border-l-4 border-orange-500" data-testid="address-info">
                  <MapPin className="w-8 h-8 text-orange-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-oswald font-bold text-lg text-slate-900 mb-2">REGISTERED OFFICE</h3>
                    <p className="text-slate-700 font-oswald text-xl mb-2">Durga Engineering Works</p>
                    <p className="text-slate-700">
                      Vill + P.O: Ghutgoria<br />
                      P.S: Barjora<br />
                      Dist: Bankura - 722202<br />
                      West Bengal, India
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-slate-50 border-l-4 border-orange-500" data-testid="phone-info">
                  <Phone className="w-8 h-8 text-orange-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-oswald font-bold text-lg text-slate-900 mb-2">PHONE</h3>
                    <a 
                      href="tel:+917364909315" 
                      className="text-slate-700 text-xl hover:text-orange-500 transition-colors font-oswald"
                    >
                      📞 +91 7364909315
                    </a>
                    <p className="text-sm text-slate-600 mt-1">Available Mon-Sat, 9:00 AM - 6:00 PM</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-slate-50 border-l-4 border-orange-500" data-testid="email-info">
                  <Mail className="w-8 h-8 text-orange-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-oswald font-bold text-lg text-slate-900 mb-2">EMAIL</h3>
                    <a 
                      href="mailto:eng.durga@gmail.com" 
                      className="text-slate-700 text-xl hover:text-orange-500 transition-colors font-oswald"
                    >
                      📧 eng.durga@gmail.com
                    </a>
                    <p className="text-sm text-slate-600 mt-1">For quotations and project inquiries</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-6 bg-slate-50 border-l-4 border-orange-500" data-testid="hours-info">
                  <Clock className="w-8 h-8 text-orange-500 flex-shrink-0" />
                  <div>
                    <h3 className="font-oswald font-bold text-lg text-slate-900 mb-2">BUSINESS HOURS</h3>
                    <p className="text-slate-700">
                      Monday - Saturday: 9:00 AM - 6:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Map & Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {/* Google Map */}
              <div className="bg-slate-100 border-l-4 border-orange-500 overflow-hidden shadow-md">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3677.8839385771976!2d87.0!3d23.25!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDE1JzAwLjAiTiA4N8KwMDAnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Durga Engineering Works Location"
                ></iframe>
              </div>

              {/* Company Highlights */}
              <div className="bg-slate-900 text-white p-8 border-l-4 border-orange-500">
                <Building2 className="w-16 h-16 text-orange-500 mb-6" />
                <h2 className="font-oswald font-bold text-3xl mb-6">WHY CHOOSE US?</h2>
                
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong className="text-orange-500">19+ Years Experience:</strong>
                      <p className="text-slate-300 text-sm mt-1">Proven track record since 2007 in structural steel fabrication</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong className="text-orange-500">500+ Workforce:</strong>
                      <p className="text-slate-300 text-sm mt-1">Skilled and semi-skilled personnel ready for large-scale projects</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong className="text-orange-500">500 MT/Month Capacity:</strong>
                      <p className="text-slate-300 text-sm mt-1">Heavy and rolled section fabrication capabilities</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong className="text-orange-500">Safety First:</strong>
                      <p className="text-slate-300 text-sm mt-1">Strict adherence to safety protocols and quality standards</p>
                    </div>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-orange-500 mt-2 mr-3 flex-shrink-0"></span>
                    <div>
                      <strong className="text-orange-500">Pan-India Presence:</strong>
                      <p className="text-slate-300 text-sm mt-1">Successfully executed projects across multiple states</p>
                    </div>
                  </li>
                </ul>

                <div className="mt-8 pt-8 border-t border-slate-700">
                  <p className="text-slate-300 text-sm mb-4">
                    For detailed quotations and project discussions:
                  </p>
                  <a
                    href="/quotation"
                    data-testid="contact-cta-btn"
                    className="bg-orange-500 hover:bg-orange-600 text-white font-bold font-oswald px-8 py-3 inline-block transition-all"
                  >
                    REQUEST QUOTATION
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
